import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  Phone,
  MessageCircle,
  Mail,
  ArrowLeft,
  ArrowLeftRight,
  Eye,
  Edit3,
  LogOut,
  CalendarClock,
  Wrench,
  LifeBuoy,
  Receipt,
  Plus,
  Send,
  Clock,
  CheckCircle2,
  AlertOctagon,
  Activity,
  MapPin,
  ShoppingCart,
  ChevronDown,
  ChevronRight,
  Bell,
  Sun,
  Moon,
  Monitor,
  BarChart3,
  Megaphone,
} from "lucide-react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useBranding } from "../context/TenantBrandingContext";
import { useTheme } from "../context/ThemeContext";
import { usePortalPerms } from "../context/PortalPermsContext";
import Modal from "../components/Modal";
import PhaseChip from "../components/PhaseChip";
import { phoneToWa, mailtoUrl } from "../lib/contact";

import AccountBlock from "../components/customer-blocks/AccountBlock";
import GalleryBlock from "../components/customer-blocks/GalleryBlock";
import JobCardsBlock from "../components/customer-blocks/JobCardsBlock";
import TicketsBlock from "../components/customer-blocks/TicketsBlock";
import PortalDocumentsFoldable from "../components/portal/PortalDocumentsFoldable";
import PortalAnnouncementBanner from "../components/portal/PortalAnnouncementBanner";
import CustomerAnnouncementsSection from "../components/customer-blocks/CustomerAnnouncementsSection";
import FoldBlock from "../components/customer-blocks/FoldBlock";
import PerformanceBlock from "../components/perf/PerformanceBlock";
import PortalTeamBlock from "../components/portal/PortalTeamBlock";
import PortalOrderSummaryBlock from "../components/portal/PortalOrderSummaryBlock";
import PortalProfileEditorBlock from "../components/portal/PortalProfileEditorBlock";
import PortalSectionsDnd from "../components/portal/PortalSectionsDnd";
import EquipmentBlock from "../components/customer-blocks/EquipmentBlock";
import SyncButton from "../components/SyncButton";
import NotificationsBell from "../components/NotificationsBell";
import VoiceTextarea from "../components/VoiceTextarea";

/**
 * Unified portal page used by:
 *   - Real clients   → /portal             (uses user.customer_id, mode=view, isPreview=false)
 *   - Manager preview → /portal/preview/:cid (mode toggleable, isPreview=true)
 */
export default function CustomerPortalView({ isPreview = false }) {
  const { customerId: urlCustomerId } = useParams();
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  const { theme, mode: themeMode, setMode: setThemeMode } = useTheme();
  // Iter 427.p44 — Server-authoritative perms. usePortalPerms() reads
  // /portal/me/perms on mount; the returned {perms, loading} drive the
  // section-visibility gate below, side-stepping any localStorage-stale
  // user.effective_perms for a maintenance-scoped user.
  const permsCtx = usePortalPerms();
  const navigate = useNavigate();

  const BRAND_NAME = branding?.app_name || "Rogue";
  // [Iter 268] Brand logo branches by theme — the same logo
  // (white-on-transparent) can't render legibly on a light surface,
  // so we resolve to a per-theme variant. Branding API may supply
  // `logo_url_light` and `logo_url_dark`. When BOTH are nullish we
  // intentionally do NOT fall back to a public Rogue PNG — that
  // image leaks the wrong brand on tenant hosts and breaks mobile
  // layout (it sizes to its own intrinsic width and overflows). The
  // hero renders a text-only mark in that case.
  // Reads from useTheme() so a portal user flipping the toggle
  // re-renders with the correct logo variant immediately.
  const BRAND_LOGO =
    theme === "light"
      ? (branding?.logo_url_light || branding?.logo_url || null)
      : (branding?.logo_url_dark || branding?.logo_url || null);
  const BRAND_TAGLINE = branding?.tagline || "Service portal";
  // [Iter 290zp.13w — Per-tenant contact info]
  // Until the Company Profile settings page exists, contact info is
  // host-gated. HV host gets hardcoded HidroVerse values. Other tenants
  // get whatever the tenant has populated under branding.support_*;
  // if NOTHING is configured, the buttons render disabled rather than
  // pointing at a cross-tenant default. TODO build Settings → Company
  // Profile so each tenant can self-serve.
  const _hostIsHV = typeof window !== "undefined" &&
    ((window.location.host || "").toLowerCase().includes("hidroverse"));
  const COMPANY_NAME = _hostIsHV
    ? "HidroVerse"
    : (branding?.company_name || "Rogue");
  const SUPPORT_WA_RAW = _hostIsHV
    ? "27723207326"
    : (branding?.support_whatsapp || branding?.support_phone || "").replace(/[^0-9]/g, "");
  const SUPPORT_TEL_RAW = _hostIsHV
    ? "+27723207326"
    : (branding?.support_phone || "");
  const SUPPORT_EMAIL_RAW = _hostIsHV
    ? "nelspruit@hidroverse.co.za"
    : (branding?.support_email || "");

  const customerId = isPreview ? urlCustomerId : (user?.customer_id_scope || user?.customer_id);

  // Iter 426 Phase 3 — Fetch this portal user's memberships once so
  // we can show the "Switch customer" shortcut ONLY when there's
  // actually more than one customer to switch to.
  const [memberships, setMemberships] = useState([]);
  useEffect(() => {
    if (isPreview) return;
    if (user?.role !== "client") return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/portal/memberships");
        if (alive) setMemberships(data?.items || []);
      } catch (_e) { /* silent */ }
    })();
    return () => { alive = false; };
  }, [user?.id, user?.role, isPreview]);
  const hasMultipleMemberships = memberships.length > 1;

  // Mode: only relevant in preview. Real clients are always "view".
  const [mode, setMode] = useState("view"); // view | edit
  const isEdit = isPreview && mode === "edit";

  const [customer, setCustomer] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [salesDocs, setSalesDocs] = useState([]);
  const [galleryFolders, setGalleryFolders] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [chatMedia, setChatMedia] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [equipmentSites, setEquipmentSites] = useState([]);
  const [equipmentLogs, setEquipmentLogs] = useState([]);
  const [removalReqs, setRemovalReqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removalTarget, setRemovalTarget] = useState(null);

  const isClient = user?.role === "client";

  // Gate the preview behind a verified password (set by /customer/{id}'s preview launcher)
  useEffect(() => {
    if (isPreview && !sessionStorage.getItem("hv_portal_preview_ok")) {
      navigate(`/customers/${customerId}`);
    }
  }, [isPreview, customerId, navigate]);

  const load = useCallback(async () => {
    if (!customerId) return;
    // Iter 427.p40 — Portal user perms drive which list fetches we
    // dispatch. Firing a jobcards fetch as an accounts-preset user
    // now returns 403 (POPIA gate), which was crashing the Overview
    // via the ErrorBoundary. Skipping the fetches also matches the
    // "tab removed from screen" spec — no request, no data.
    // Iter 427.p44 — Use server-authoritative perms from
    // PortalPermsContext (fresh from /portal/me/perms). Falls back to
    // user.effective_perms only for staff preview mode where the
    // provider isn't mounted.
    const ctxPerms = permsCtx?.perms || null;
    const _perms = isClient
      ? ctxPerms
      : (user && (user.effective_perms || user.portal_permissions)) || null;
    const _can = (k) => {
      if (!isClient) return true;   // staff / preview mode → all
      if (!_perms) return false;    // fail closed — perms not yet loaded
      return _perms[k] === true;
    };
    const NOOP = Promise.resolve({ data: [] });
    const qs = `?customer_id=${customerId}`;
    const results = await Promise.allSettled([
      api.get(`/customers/${customerId}`),
      _can("portal.jobs.read")    ? api.get(`/jobcards${qs}`)   : NOOP,
      _can("portal.tickets.read") ? api.get(`/tickets${qs}`)    : NOOP,
      (_can("portal.quotes.read") || _can("portal.invoices.read"))
        ? api.get(`/sales-documents${qs}`) : NOOP,
      _can("portal.photos.read")  ? api.get(`/gallery-folders${qs}`) : NOOP,
      _can("portal.photos.read")  ? api.get(`/gallery-items${qs}`)   : NOOP,
      _can("portal.orders.write") ? api.get(`/equipment${qs}`)  : NOOP,
      _can("portal.orders.write") ? api.get(`/removal-requests${qs}`) : NOOP,
      api.get(`/customers/${customerId}/chat-media`),
      _can("portal.orders.write") ? api.get(`/sites${qs}`)      : NOOP,
      _can("portal.jobs.read")    ? api.get(`/service-logs${qs}`) : NOOP,
    ]);
    const [c, j, t, sd, gf, gi, eq, rr, cm, ss, sl] = results.map((r) =>
      r.status === "fulfilled" ? r.value : { data: null, _error: r.reason }
    );
    if (c.data) setCustomer(c.data);
    setJobs(Array.isArray(j.data) ? j.data : []);
    setTickets(Array.isArray(t.data) ? t.data : []);
    // Wave 32 (Iter 233) — portal must NEVER expose DNU (internal) sales
    // docs to a client. Real-client requests are already audience-filtered
    // server-side; preview mode hits the staff endpoint which returns
    // everything, so we strip internal docs here too. Belt and braces.
    const rawSales = Array.isArray(sd.data) ? sd.data : [];
    setSalesDocs(rawSales.filter((d) => (d?.audience || "customer") !== "internal"));
    setGalleryFolders(Array.isArray(gf.data) ? gf.data : []);
    setGalleryItems(Array.isArray(gi.data) ? gi.data : []);
    setChatMedia(Array.isArray(cm.data) ? cm.data : []);
    setEquipment(Array.isArray(eq.data) ? eq.data : []);
    setEquipmentSites(Array.isArray(ss.data) ? ss.data : []);
    setEquipmentLogs(Array.isArray(sl.data) ? sl.data : []);
    setRemovalReqs(Array.isArray(rr.data) ? rr.data : []);
    setLoading(false);
  }, [customerId, isPreview, permsCtx?.perms, permsCtx?.loading, user?.id]);

  useEffect(() => {
    setLoading(true);
    // Hard timeout safety-net: even if every individual request hangs
    // past its 60 s axios timeout, the spinner clears after 75 s and
    // the user sees an empty state instead of an infinite "Loading…".
    const stuckTimer = setTimeout(() => setLoading(false), 75_000);
    load().finally(() => clearTimeout(stuckTimer));
  }, [load]);

  // Refresh gallery lists when a child component (e.g. gallery multi-delete)
  // dispatches a "gallery:refresh" window event.
  useEffect(() => {
    const onRefresh = () => load();
    window.addEventListener("gallery:refresh", onRefresh);
    return () => window.removeEventListener("gallery:refresh", onRefresh);
  }, [load]);

  // For "view" mode in the preview, mimic the client experience by filtering hidden items.
  // The backend already filters for real clients.
  const visibleJobs = isEdit ? jobs : jobs.filter((j) => !j.hidden_from_client);
  const visibleTickets = isEdit ? tickets : tickets.filter((t) => !t.hidden_from_client);

  // Schedule = upcoming services derived from equipment
  const scheduledServices = equipment
    .filter(
      (eq) =>
        eq.is_service_item &&
        eq.next_service_date &&
        new Date(eq.next_service_date) >= new Date(Date.now() - 24 * 60 * 60 * 1000),
    )
    .sort((a, b) => new Date(a.next_service_date) - new Date(b.next_service_date));

  const onRequestRemoval = (item) => setRemovalTarget(item);

  const submitRemoval = async (reason) => {
    if (!removalTarget) return;
    await api.post("/removal-requests", {
      customer_id: customerId,
      item_type: removalTarget.type,
      item_id: removalTarget.id,
      item_label: removalTarget.label,
      reason,
    });
    setRemovalTarget(null);
    load();
  };

  if (loading || !customer) {
    return (
      <div className="min-h-screen bg-[var(--hv-bg)] text-[var(--hv-text)] flex items-center justify-center">
        Loading…
      </div>
    );
  }

  // Quick-contact helpers — pull from CRM contacts/customers
  const ownerPhone = (customer.phone || "").replace(/[^0-9+]/g, "");
  const ownerWa = (customer.whatsapp || customer.phone || "").replace(/[^0-9]/g, "");
  const ownerEmail = customer.email || "";

  // [Iter 290zp.13w] Disable contact buttons when no tenant value is set
  // (instead of falling through to a cross-tenant default).
  const HV_SUPPORT_WA = SUPPORT_WA_RAW;
  const HV_SUPPORT_TEL = SUPPORT_TEL_RAW;
  const HV_SUPPORT_EMAIL = SUPPORT_EMAIL_RAW;

  return (
    <div className="min-h-screen bg-[var(--hv-bg)] text-[var(--hv-text)] font-sans hv-safe-top hv-safe-bottom">
      {/* Iter 418.am — global portal announcement banner. Not gated
          by preview mode; a real client viewing their own portal
          needs to see + acknowledge pending notices. */}
      {!isPreview && <PortalAnnouncementBanner />}
      {/* ============== Manager preview banner ============== */}
      {isPreview && (
        <div className="bg-gradient-to-r from-[#1fb6c1] to-[#06b6d4] text-[#0a0b0e] px-4 py-2 flex items-center justify-between gap-3 sticky top-0 z-50 shadow-lg hv-safe-top hv-safe-x flex-wrap">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider">
            <Eye className="w-4 h-4" /> Manager preview · {customer.name}
          </div>
          <div className="flex items-center gap-2">
            <div className="bg-black/20 rounded-full p-0.5 flex" data-testid="portal-mode-toggle">
              <button
                type="button"
                onClick={() => setMode("view")}
                className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold transition ${
                  mode === "view" ? "bg-white text-[#0a0b0e]" : "text-[#0a0b0e]/70 hover:text-[#0a0b0e]"
                }`}
                data-testid="portal-mode-view"
              >
                <Eye className="w-3 h-3 inline mr-1" /> View
              </button>
              <button
                type="button"
                onClick={() => setMode("edit")}
                className={`px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold transition ${
                  mode === "edit" ? "bg-white text-[#0a0b0e]" : "text-[#0a0b0e]/70 hover:text-[#0a0b0e]"
                }`}
                data-testid="portal-mode-edit"
              >
                <Edit3 className="w-3 h-3 inline mr-1" /> Edit
              </button>
            </div>
            <Link
              to={`/customers/${customerId}`}
              className="hv-btn !bg-[#0a0b0e] !text-white !border-[#0a0b0e] hover:!bg-[#1a1b1f] text-xs"
              data-testid="portal-exit-preview"
            >
              <ArrowLeft className="w-3 h-3" /> Back to CRM
            </Link>
          </div>
        </div>
      )}

      {/* ============== Brand-driven hero ============== */}
      <header className="relative overflow-hidden border-b border-[var(--hv-border)]">
        <div
          className="absolute inset-0 opacity-20 pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at 20% 0%, rgba(31,182,193,0.4) 0%, transparent 50%), radial-gradient(circle at 80% 100%, rgba(167,139,250,0.3) 0%, transparent 50%)",
          }}
        />
        <div className="absolute -top-32 -right-20 w-96 h-96 rounded-full bg-[#1fb6c1]/10 blur-3xl pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-4 md:px-8 py-4 md:py-6">
          {/* [Iter 290zp.13z2] Horizontal hero — uses width not height.
              Row 1: logo (left) + two text lines beside it
                ("HidroVerse Customer Portal" / "Welcome, <name>")
              Row 2: full-width paragraph + Customer ID
              Row 3: icon strip — Theme, WhatsApp, Email, Call, Bell,
                     Refresh, Sign-out
              Row 4: action buttons (Log a ticket · Place an order)
              Row 5: KPIs grid (Open tickets · Active job cards)
              Goal: collapse vertical real-estate so KPIs land above
              the fold even on a narrow phone. */}
          <div className="flex items-center gap-3">
            {BRAND_LOGO ? (
              <img
                src={BRAND_LOGO}
                alt={BRAND_NAME}
                className="h-12 sm:h-14 md:h-16 w-auto max-w-[90px] sm:max-w-[120px] md:max-w-[160px] object-contain shrink-0"
                data-testid="portal-logo"
              />
            ) : (
              <div
                className="shrink-0 font-display text-lg sm:text-xl font-bold tracking-tight text-[var(--hv-text)]"
                data-testid="portal-logo-text"
              >
                {BRAND_NAME}
              </div>
            )}
            <div className="min-w-0 flex-1 leading-tight">
              <div className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[#1fb6c1] font-bold truncate">
                {COMPANY_NAME && COMPANY_NAME !== "Rogue" ? `${COMPANY_NAME} Customer Portal` : "Customer Portal"}
              </div>
              <h1 className="font-display text-base sm:text-xl md:text-2xl font-bold tracking-tight break-words mt-0.5">
                Welcome,{" "}
                <span className="bg-gradient-to-r from-[#1fb6c1] to-[#A78BFA] bg-clip-text text-transparent">
                  {customer.name}
                </span>
              </h1>
            </div>
          </div>

          <div className="text-xs sm:text-sm text-[var(--hv-text-secondary)] mt-2">
            Your service portal. Track jobs, tickets, sales documents, and
            site media in one place — and reach our team in seconds.
          </div>
          {customer.external_ref && (
            <div className="text-[10px] font-mono text-[var(--hv-text-muted)] mt-1">
              Customer ID: {customer.external_ref}
            </div>
          )}

          {/* Row 3: icon strip — Theme · WhatsApp · Email · Call · Bell · Refresh · Sign-out */}
          <div className="mt-3 flex items-center gap-1.5 flex-wrap" data-testid="portal-icon-strip">
            <PortalThemeToggle
              mode={themeMode}
              resolved={theme}
              onChange={setThemeMode}
            />
            {HV_SUPPORT_WA ? (
              <a
                href={`https://wa.me/${HV_SUPPORT_WA}`}
                target="_blank"
                rel="noopener noreferrer"
                className="hv-btn hv-btn-ghost p-2"
                title={`WhatsApp ${COMPANY_NAME}`}
                aria-label={`WhatsApp ${COMPANY_NAME}`}
                data-testid="portal-contact-whatsapp"
              >
                <MessageCircle className="w-4 h-4" />
              </a>
            ) : null}
            {HV_SUPPORT_EMAIL ? (
              <a
                href={mailtoUrl({ to: HV_SUPPORT_EMAIL })}
                className="hv-btn hv-btn-ghost p-2"
                title="Open in your mail app"
                aria-label="Email us"
                data-testid="portal-contact-email"
              >
                <Mail className="w-4 h-4" />
              </a>
            ) : null}
            {HV_SUPPORT_TEL ? (
              <a
                href={`tel:${HV_SUPPORT_TEL}`}
                className="hv-btn hv-btn-ghost p-2"
                title="Tap to call"
                aria-label="Call us"
                data-testid="portal-contact-call"
              >
                <Phone className="w-4 h-4" />
              </a>
            ) : null}
            <NotificationsBell enabled />
            {!isPreview && <SyncButton />}
            {!isPreview && hasMultipleMemberships && (
              <button
                type="button"
                onClick={async () => {
                  await logout();
                  navigate("/login?switch=1");
                }}
                className="hv-btn hv-btn-ghost p-2"
                title={`Switch customer (you're a member of ${memberships.length} customers)`}
                aria-label="Switch customer"
                data-testid="portal-switch-customer"
              >
                <ArrowLeftRight className="w-4 h-4" />
              </button>
            )}
            {!isPreview && (
              <button
                type="button"
                onClick={async () => {
                  await logout();
                  navigate("/login");
                }}
                className="hv-btn hv-btn-ghost p-2"
                title="Sign out"
                aria-label="Sign out"
                data-testid="portal-logout"
              >
                <LogOut className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Row 4: primary action buttons */}
          <div className="mt-3 grid grid-cols-3 gap-2" data-testid="portal-quick-actions">
            <Link
              to={isPreview ? `/portal/tickets/new?customer_id=${customerId}&as_staff=1` : `/portal/tickets/new?customer_id=${customerId}`}
              className="hv-btn hv-btn-ghost w-full justify-center"
              style={{ borderColor: "#F43F5E", color: "#F43F5E" }}
              data-testid="portal-new-ticket-btn"
              title={isPreview ? `Log a ticket for ${customer.name}` : "Log a support ticket"}
            >
              <LifeBuoy className="w-4 h-4" /> Log a ticket
            </Link>
            <Link
              to={isPreview ? `/portal/order-new?customer_id=${customerId}&as_staff=1` : `/portal/order-new?customer_id=${customerId}`}
              className="hv-btn hv-btn-ghost w-full justify-center"
              style={{ borderColor: "#F59E0B", color: "#F59E0B" }}
              data-testid="portal-order-now-hero-btn"
              title={isPreview ? `Place an order for ${customer.name}` : "Place an order"}
            >
              <ShoppingCart className="w-4 h-4" /> Place an order
            </Link>
            <Link
              to={isPreview ? `/portal/performance?customer_id=${customerId}` : "/portal/performance"}
              className="hv-btn hv-btn-ghost w-full justify-center"
              style={{ borderColor: "#22D3EE", color: "#22D3EE" }}
              data-testid="portal-performance-btn"
              title="Daily performance readings"
            >
              <BarChart3 className="w-4 h-4" /> Performance
            </Link>
          </div>
        </div>
      </header>

      {/* ============== Body ============== */}
      <main className="max-w-6xl mx-auto px-4 md:px-8 py-6 hv-fade-in space-y-4">
        {/* KPIs — [Iter 290zp.13z] Trimmed to the two streams customers
            actually act on (jobs + tickets). Documents/Upcoming
            services live further down the page; the KPI tiles were
            adding visual noise without much value above the fold. */}
        <div className="grid grid-cols-2 gap-3" data-testid="portal-kpis">
          <Kpi
            label="Active jobs"
            value={visibleJobs.filter((j) => !["closed", "cancelled", "paid"].includes(j.phase)).length}
            accent="#A78BFA"
            icon={Wrench}
          />
          <Kpi
            label="Open tickets"
            value={visibleTickets.filter((t) => !["resolved_via_jc", "resolved_external", "closed", "cancelled", "resolved"].includes(t.status)).length}
            accent="#F43F5E"
            icon={LifeBuoy}
          />
        </div>

        {/* Quick action: log a ticket — now lives in the header. */}

        {/* Drag-drop reorderable sections (client-only persists order). */}
        {/* [Iter 290zp.13y] Permission-gated sections — mirrors the CRM
            side's pattern of REMOVING blocks from the DOM when the
            user's role doesn't include the relevant capability. Reads
            `user.effective_perms` from /auth/me (computed by
            lib/portal_perms.effective_portal_perms). Owner / admin
            presets get everything; maintenance / accounts presets get
            only what their role allows. Falls back to all-true if the
            field is missing on the user object (manager preview or
            legacy users without explicit role assignment). */}
        {(() => {
          // Iter 427.p44 (2026-02-20) — Server-authoritative perm gate.
          // Reads from PortalPermsContext which fetches
          // `/portal/me/perms` on mount (fresh from server, respects
          // the SCOPED membership preset). This replaces the previous
          // read of `user.effective_perms` which could be stale in
          // localStorage for a maintenance-scoped user on their first
          // multi-membership login — leading to the reported
          // "signed in as maintenance but still see Documents / Account"
          // POPIA leak. `user.effective_perms` is kept as a fallback
          // only for manager preview mode where PortalPermsProvider
          // isn't mounted.
          const ctxPerms = permsCtx?.perms || null;
          const legacyPerms = user?.effective_perms || user?.portal_permissions || null;
          const _perms = isClient ? ctxPerms : legacyPerms;
          const _can = (k) => {
            if (isPreview) return true;
            if (!_perms) return false;
            return _perms[k] === true;
          };
          return (
        <PortalSectionsDnd
          storageKey={`hv:portal-order:${customerId}`}
          enabled={isClient}
          sections={[
            // Iter 392 — Order: Log sheet → Performance analytics
            // (inside PerformanceBlock) → Support tickets → Job cards
            // → Orders → Gallery → the rest.
            // Iter 427.p45 (Dirk, 2026-02-20) — Single render path for
            // the performance block. Both `capture` (log-sheet input)
            // and `read` (analytics view) come off the scoped preset;
            // maintenance now has BOTH by default so a maintenance-
            // scoped user can log daily readings AND view analytics.
            // The old else-branch hard-coded `canCapture={isPreview}`
            // which silently disabled capture for anyone without
            // `portal.performance.read` — an unrelated perm.
            ...(_can("portal.performance.read") || _can("portal.performance.capture") ? [{
              id: "performance",
              render: () => (
                <PerformanceBlock
                  customerId={customerId}
                  canEdit={false}
                  canCapture={_can("portal.performance.capture") || isPreview}
                  defaultOpen={false}
                  isPortal
                />
              ),
            }] : []),
            {
              id: "tickets",
              render: () => (
                <TicketsBlock
                  tickets={visibleTickets}
                  canEdit={isEdit}
                  previewEditMode={isEdit}
                  onChanged={load}
                  isPortal
                  customerId={customerId}
                  customerName={customer.name}
                />
              ),
            },
            // Iter 418.bi (Dirk, 2026-02-28) — Portal Announcements
            // section: customers finally get a place to see the
            // history of what staff have posted + verify their ack
            // status. Read-only from the portal side (composer stays
            // CRM-only).
            {
              id: "announcements",
              render: () => (
                <FoldBlock
                  icon={Megaphone}
                  iconColor="#22d3ee"
                  title="Announcements"
                  testId="portal-block-announcements"
                  defaultOpen={false}
                  emptyHint="No announcements from your service team yet."
                >
                  <CustomerAnnouncementsSection customer={{ id: customerId, name: customer.name }} readOnly />
                </FoldBlock>
              ),
            },
            // Iter 398 — Portal Equipment: per-site list + service
            // history. No schedule; "Reorder" deep-links each row to
            // the existing /portal/order-new catalogue.
            {
              id: "equipment",
              render: () => (
                <EquipmentBlock
                  equipment={equipment}
                  serviceLogs={equipmentLogs}
                  sites={equipmentSites}
                  canEdit={false}
                  onChanged={load}
                  isPortal
                  customerId={customerId}
                />
              ),
            },
            {
              id: "jobcards",
              render: () => (
                <JobCardsBlock
                  jobs={visibleJobs}
                  sites={[]}
                  canEdit={isEdit}
                  previewEditMode={isEdit}
                  onChanged={load}
                  isPortal
                  isPreview={isPreview}
                  customerId={customerId}
                  customerName={customer.name}
                />
              ),
            },
            ...((isClient || isPreview) && _can("portal.orders.write") ? [{
              id: "orders",
              render: () => (
                <PortalOrderSummaryBlock customerId={customerId} isPreview={isPreview} />
              ),
            }] : []),
            ...(_can("portal.photos.read") ? [{
              id: "gallery",
              render: () => (
                <GalleryBlock
                  customerId={customerId}
                  folders={galleryFolders}
                  items={galleryItems}
                  jobcards={visibleJobs}
                  tickets={visibleTickets}
                  chats={chatMedia}
                  canEdit={isEdit}
                  canUpload={isEdit || isClient}
                  canDelete={isEdit}
                  canRequestRemoval={isClient}
                  isPortal
                  onChanged={load}
                  onRequestRemoval={onRequestRemoval}
                />
              ),
            }] : []),
            ...(_can("portal.jobs.read") || _can("portal.tickets.read") ? [{
              id: "scheduled",
              render: () => (
                <ScheduledServicesBlock services={scheduledServices} isEdit={isEdit} />
              ),
            }] : []),
            ...((isClient || isPreview) && _can("portal.profile.read") ? [{
              id: "profile",
              render: () => (
                <PortalProfileEditorBlock
                  customer={customer}
                  canEdit={(isClient && _can("portal.profile.write")) || isEdit}
                  onChanged={load}
                  isPreview={isPreview}
                />
              ),
            }] : []),
            ...(_can("portal.documents.read") ? [{
              id: "documents",
              render: () => (
                <PortalDocumentsFoldable
                  customerId={customerId}
                  apiBase={isClient ? "/portal" : `/customers/${customerId}`}
                  canEdit={false}
                  canDelete={false}
                />
              ),
            }] : []),
            ...((_can("portal.quotes.read") || _can("portal.invoices.read")) ? [{
              id: "account",
              render: () => (
                <AccountBlock
                  customerId={customerId}
                  documents={salesDocs}
                  canEdit={isEdit || isClient}
                  canDelete={isEdit || isClient}
                  canMarkPaid={isEdit}
                  canRequestRemoval={isClient}
                  currentUserId={isClient ? user?.id : null}
                  isPortal
                  hideTotals
                  onChanged={load}
                  onRequestRemoval={onRequestRemoval}
                />
              ),
            }] : []),
            ...((isPreview || removalReqs.some((r) => r.status === "pending")) ? [{
              id: "removal-requests",
              render: () => (
                <RemovalRequestsBlock
                  requests={removalReqs}
                  isManager={isPreview}
                  onChanged={load}
                />
              ),
            }] : []),
            ...((isClient || isPreview) ? [{
              id: "team",
              render: () => (
                <PortalTeamBlock currentUserId={user?.id} isPreview={isPreview} customerId={customerId} />
              ),
            }] : []),
          ]}
        />
          );
        })()}

        {/* Footer */}
        <footer className="text-center text-[10px] uppercase tracking-[0.3em] text-[var(--hv-text-muted)] py-8">
          Powered by <span className="text-[#1fb6c1] font-bold">Geminii Space</span>
        </footer>
      </main>

      {/* Removal request modal */}
      {removalTarget && (
        <RemovalRequestModal
          target={removalTarget}
          onClose={() => setRemovalTarget(null)}
          onSubmit={submitRemoval}
        />
      )}
    </div>
  );
}

/* ---------- Bits ---------- */
function PortalThemeToggle({ mode, resolved, onChange }) {
  // Compact 3-way toggle (auto / light / dark) for portal users.
  // Mirrors the CRM ThemeTogglePanel but tuned for an inline header
  // slot — single icon button that cycles modes and shows the active
  // one. Hover reveals the inline 3-segment picker for direct choice.
  const ResolvedIcon = resolved === "dark" ? Moon : Sun;
  const ActiveIcon = mode === "auto" ? Monitor : ResolvedIcon;
  const tooltip =
    mode === "auto"
      ? `Theme: Auto (${resolved})`
      : `Theme: ${mode === "light" ? "Light" : "Dark"}`;
  const cycle = () => {
    const next = mode === "auto" ? "light" : mode === "light" ? "dark" : "auto";
    onChange(next);
  };
  const seg = (val, label, Icon) => (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onChange(val); }}
      title={label}
      className={`px-1.5 py-1 flex items-center gap-1 transition text-[10px] uppercase tracking-wider ${
        mode === val
          ? "bg-[#1fb6c1] text-black font-bold"
          : "text-[var(--hv-text-muted)] hover:text-[var(--hv-text)]"
      }`}
      data-testid={`portal-theme-${val}`}
    >
      <Icon className="w-3 h-3" />
      <span className="hidden md:inline">{label}</span>
    </button>
  );
  return (
    <>
      <div
        className="hidden md:inline-flex rounded-md border border-[var(--hv-border)] overflow-hidden bg-[var(--hv-surface)]"
        data-testid="portal-theme-toggle"
      >
        {seg("auto", "Auto", Monitor)}
        {seg("light", "Light", Sun)}
        {seg("dark", "Dark", Moon)}
      </div>
      {/* Mobile: single cycling button to save horizontal real estate */}
      <button
        type="button"
        onClick={cycle}
        title={tooltip}
        className="md:hidden w-8 h-8 inline-flex items-center justify-center rounded-md border border-[var(--hv-border)] bg-[var(--hv-surface)] text-[var(--hv-text-secondary)] hover:text-[var(--hv-text)]"
        data-testid="portal-theme-cycle"
        aria-label={tooltip}
      >
        <ActiveIcon className="w-4 h-4" />
      </button>
    </>
  );
}

function Kpi({ label, value, accent, icon: Icon }) {
  return (
    <div
      className="hv-card p-4 flex items-center gap-3"
      style={{ borderTop: `2px solid ${accent}` }}
    >
      <div
        className="w-10 h-10 rounded-md flex items-center justify-center shrink-0"
        style={{ background: `${accent}1f`, border: `1px solid ${accent}55`, color: accent }}
      >
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="font-display text-2xl font-bold leading-none" style={{ color: accent }}>
          {value}
        </div>
        <div className="text-[10px] uppercase tracking-wider text-[var(--hv-text-muted)] mt-1">{label}</div>
      </div>
    </div>
  );
}

function ScheduledServicesBlock({ services, isEdit }) {
  const [open, setOpen] = useState(false);
  if (services.length === 0) return null;
  return (
    <section className="hv-card p-4" data-testid="portal-scheduled-services">
      <header
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="w-4 h-4 text-[var(--hv-text-muted)]" /> : <ChevronRight className="w-4 h-4 text-[var(--hv-text-muted)]" />}
          <CalendarClock className="w-4 h-4 text-[#1fb6c1]" />
          <h3 className="font-display text-base font-bold">Scheduled services</h3>
          <span className="text-[10px] font-mono px-2 py-0.5 rounded-full border border-[#1fb6c1]/40 text-[#1fb6c1] bg-[#1fb6c1]/5">
            {services.length}
          </span>
        </div>
      </header>
      {open && (
        <div className="mt-3 space-y-1.5">
          {services.slice(0, 12).map((s) => {
            const d = new Date(s.next_service_date);
            const isOverdue = d < new Date();
            return (
              <div
                key={s.id}
                className={`flex items-center justify-between gap-2 px-3 py-2 rounded border text-xs ${
                  isOverdue
                    ? "border-rose-500/30 bg-rose-500/5"
                    : "border-[#1fb6c1]/20 bg-[#1fb6c1]/5"
                }`}
                data-testid={`portal-service-${s.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{s.name}</div>
                  <div className="text-[10px] text-[var(--hv-text-muted)] truncate">
                    {s.quantity || 0} {s.unit || "pcs"}
                  </div>
                </div>
                <div
                  className={`text-[10px] font-mono shrink-0 ${
                    isOverdue ? "text-rose-300" : "text-[#1fb6c1]"
                  }`}
                >
                  {d.toLocaleDateString('en-GB')}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function RemovalRequestsBlock({ requests, isManager, onChanged }) {
  const [busy, setBusy] = useState(null);
  const pending = requests.filter((r) => r.status === "pending");
  const resolved = requests.filter((r) => r.status !== "pending");
  if (requests.length === 0) return null;

  const approve = async (rid) => {
    setBusy(rid);
    try {
      await api.post(`/removal-requests/${rid}/approve`);
      onChanged?.();
    } finally {
      setBusy(null);
    }
  };
  const reject = async (rid) => {
    const note = window.prompt("Reason for rejecting?") || "";
    setBusy(rid);
    try {
      await api.post(`/removal-requests/${rid}/reject`, { note });
      onChanged?.();
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="hv-card p-4" data-testid="portal-removal-requests">
      <header className="flex items-center gap-2 mb-3">
        <AlertOctagon className="w-4 h-4 text-amber-300" />
        <h3 className="font-display text-base font-bold">Removal requests</h3>
        {pending.length > 0 && (
          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border border-amber-500/40 text-amber-300 bg-amber-500/10">
            {pending.length} pending
          </span>
        )}
      </header>
      <ul className="space-y-1.5">
        {[...pending, ...resolved].slice(0, 20).map((r) => (
          <li
            key={r.id}
            className="flex items-center justify-between gap-2 px-3 py-2 rounded border border-white/5 bg-white/[0.02] text-xs"
          >
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate">
                Remove: {r.item_label || `${r.item_type} #${r.item_id}`}
              </div>
              <div className="text-[10px] text-[var(--hv-text-muted)]">
                Requested by {r.requested_by_name} ·{" "}
                {new Date(r.created_at).toLocaleString('en-GB')}
                {r.reason && <> · "{r.reason}"</>}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {r.status === "pending" && isManager && (
                <>
                  <button
                    type="button"
                    onClick={() => approve(r.id)}
                    disabled={busy === r.id}
                    className="hv-btn hv-btn-primary text-[10px] px-2 py-0.5"
                    data-testid={`removal-approve-${r.id}`}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => reject(r.id)}
                    disabled={busy === r.id}
                    className="hv-btn hv-btn-ghost text-[10px] px-2 py-0.5"
                    data-testid={`removal-reject-${r.id}`}
                  >
                    Reject
                  </button>
                </>
              )}
              {r.status !== "pending" && (
                <span
                  className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                    r.status === "approved"
                      ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/5"
                      : "border-rose-500/40 text-rose-300 bg-rose-500/5"
                  }`}
                >
                  {r.status}
                </span>
              )}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function RemovalRequestModal({ target, onClose, onSubmit }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  return (
    <Modal
      open
      onClose={onClose}
      title="Request removal"
      testId="removal-request-modal"
      maxWidth="max-w-md"
    >
      <div className="space-y-3" data-testid="removal-request-form">
        <div className="text-sm">
          You can't remove items yourself — your manager will handle this.
          Submitting will notify them.
        </div>
        <div className="px-3 py-2 rounded border border-[var(--hv-border)] bg-white/[0.02] text-xs">
          <span className="text-[var(--hv-text-muted)]">Item:</span>{" "}
          <span className="font-medium">{target.label}</span>{" "}
          <span className="text-[var(--hv-text-muted)]">({target.type})</span>
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider text-[var(--hv-text-secondary)]">Reason (optional)</label>
          <VoiceTextarea
            value={reason}
            onChange={setReason}
            placeholder="Why should this be removed?"
            data-testid="removal-request-reason"
           testIdPrefix="removal-request-reason-voice" context="note" />
        </div>
        <button
          type="button"
          onClick={async () => {
            setBusy(true);
            try {
              await onSubmit(reason);
            } finally {
              setBusy(false);
            }
          }}
          disabled={busy}
          className="hv-btn hv-btn-primary w-full"
          data-testid="removal-request-submit"
        >
          <Send className="w-4 h-4" /> {busy ? "Sending…" : "Send removal request"}
        </button>
      </div>
    </Modal>
  );
}
